import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { RichTextEditor } from '../shared/RichTextEditor';
import { CreatePracticeQuestionRequest, PracticeQuestion } from '../../types/lesson';

type QuestionType = PracticeQuestion['question_type'];
type Difficulty = PracticeQuestion['difficulty'];
type WordProblemType = NonNullable<PracticeQuestion['word_problem_type']>;

interface ManualQuestionDialogProps {
  open: boolean;
  unitTitle: string;
  initialQuestion?: PracticeQuestion | null;
  title?: string;
  onClose: () => void;
  onSave: (question: CreatePracticeQuestionRequest['question']) => Promise<void>;
}

interface ManualQuestionFormState {
  questionType: QuestionType;
  difficulty: Difficulty;
  questionText: string;
  learningObjective: string;
  points: string;
  explanation: string;
  hints: string;
  options: string[];
  correctOptionIndex: number;
  trueFalseAnswer: boolean;
  shortAnswer: string;
  feedbackEnabled: boolean;
  feedbackRubric: string;
  numericAnswer: string;
  tolerance: string;
  unit: string;
  wordProblemContext: string;
  wordProblemType: WordProblemType;
}

const defaultForm = (unitTitle: string): ManualQuestionFormState => ({
  questionType: 'multiple_choice',
  difficulty: 'medium',
  questionText: '',
  learningObjective: unitTitle,
  points: '1',
  explanation: '',
  hints: '',
  options: ['', '', '', ''],
  correctOptionIndex: 0,
  trueFalseAnswer: true,
  shortAnswer: '',
  feedbackEnabled: false,
  feedbackRubric: '',
  numericAnswer: '',
  tolerance: '0.01',
  unit: '',
  wordProblemContext: '',
  wordProblemType: 'single_step',
});

const formFromQuestion = (
  question: PracticeQuestion | null | undefined,
  unitTitle: string,
): ManualQuestionFormState => {
  if (!question) {
    return defaultForm(unitTitle);
  }

  const options = question.options?.length
    ? [...question.options.map((option) => option.text), '', '', ''].slice(0, 4)
    : ['', '', '', ''];
  const correctOptionIndex = Math.max(
    0,
    question.options?.findIndex(
      (option) => option.is_correct || option.text === question.correct_answer,
    ) ?? 0,
  );
  const numericAnswer =
    question.question_type === 'numeric' || question.question_type === 'word_problem'
      ? String(question.correct_answer ?? '')
      : '';

  return {
    questionType: question.question_type,
    feedbackEnabled: question.feedback_enabled || false,
    feedbackRubric: question.feedback_rubric || '',
    difficulty: question.difficulty,
    questionText: question.question_text,
    learningObjective: question.learning_objective || unitTitle,
    points: String(question.points ?? 1),
    explanation: question.explanation || '',
    hints: question.hints?.join('\n') || '',
    options,
    correctOptionIndex,
    trueFalseAnswer:
      typeof question.correct_answer === 'boolean'
        ? question.correct_answer
        : String(question.correct_answer).toLowerCase() === 'true',
    shortAnswer:
      question.question_type === 'short_answer' ? String(question.correct_answer ?? '') : '',
    numericAnswer,
    tolerance: String(question.tolerance ?? 0.01),
    unit: question.unit || '',
    wordProblemContext: question.word_problem_context || '',
    wordProblemType: question.word_problem_type || 'single_step',
  };
};

const stripHtml = (value: string) =>
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const hasRichQuestionContent = (value: string) =>
  Boolean(
    stripHtml(value) ||
      /<img\b/i.test(value) ||
      /data-type=["'](?:inline-math|block-math)["']/i.test(value),
  );

export const ManualQuestionDialog: React.FC<ManualQuestionDialogProps> = ({
  open,
  unitTitle,
  initialQuestion = null,
  title = 'Add Practice Question',
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState<ManualQuestionFormState>(() =>
    formFromQuestion(initialQuestion, unitTitle),
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(formFromQuestion(initialQuestion, unitTitle));
      setValidationError(null);
    }
  }, [open, initialQuestion, unitTitle]);

  const answerLabel = useMemo(() => {
    if (form.questionType === 'numeric' || form.questionType === 'word_problem') {
      return 'Correct numeric answer';
    }

    return 'Correct answer';
  }, [form.questionType]);

  const updateField = <Field extends keyof ManualQuestionFormState>(
    field: Field,
    value: ManualQuestionFormState[Field],
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateOption = (index: number, value: string) => {
    setForm((current) => ({
      ...current,
      options: current.options.map((option, optionIndex) =>
        optionIndex === index ? value : option,
      ),
    }));
  };

  const buildQuestion = (): CreatePracticeQuestionRequest['question'] | null => {
    if (!hasRichQuestionContent(form.questionText)) {
      setValidationError('Question text is required.');
      return null;
    }

    const points = Number(form.points);
    if (!Number.isFinite(points) || points <= 0) {
      setValidationError('Points must be greater than 0.');
      return null;
    }

    const tolerance = Number(form.tolerance);
    if (
      (form.questionType === 'numeric' || form.questionType === 'word_problem') &&
      (!Number.isFinite(tolerance) || tolerance < 0)
    ) {
      setValidationError('Tolerance must be 0 or greater.');
      return null;
    }

    if (
      form.questionType === 'short_answer' &&
      form.feedbackEnabled &&
      !form.feedbackRubric.trim()
    ) {
      setValidationError('A rubric is required for teacher-reviewed AI feedback.');
      return null;
    }
    const baseQuestion = {
      feedback_enabled: form.questionType === 'short_answer' && form.feedbackEnabled,
      feedback_rubric: form.questionType === 'short_answer' ? form.feedbackRubric.trim() : '',

      question_type: form.questionType,
      question_text: form.questionText,
      difficulty: form.difficulty,
      learning_objective: form.learningObjective.trim() || unitTitle,
      points,
      explanation: form.explanation.trim(),
      hints: form.hints
        .split('\n')
        .map((hint) => hint.trim())
        .filter(Boolean),
    };

    if (form.questionType === 'multiple_choice') {
      const correctOption = form.options[form.correctOptionIndex]?.trim();
      const options = form.options
        .map((text, index) => ({ text: text.trim(), index }))
        .filter((option) => option.text)
        .map((option, index) => ({
          option_id: String.fromCharCode(65 + index),
          text: option.text,
          is_correct: option.index === form.correctOptionIndex,
        }));

      if (options.length < 2 || !correctOption) {
        setValidationError(
          'Multiple choice questions need at least two options and a correct option.',
        );
        return null;
      }

      return {
        ...baseQuestion,
        options,
        correct_answer: correctOption,
      };
    }

    if (form.questionType === 'true_false') {
      return {
        ...baseQuestion,
        correct_answer: form.trueFalseAnswer,
      };
    }

    if (form.questionType === 'numeric' || form.questionType === 'word_problem') {
      const numericAnswer = Number(form.numericAnswer);
      if (!Number.isFinite(numericAnswer)) {
        setValidationError(`${answerLabel} must be a number.`);
        return null;
      }

      return {
        ...baseQuestion,
        correct_answer: numericAnswer,
        tolerance,
        unit: form.unit.trim() || undefined,
        word_problem_context:
          form.questionType === 'word_problem'
            ? form.wordProblemContext.trim() || undefined
            : undefined,
        word_problem_type: form.questionType === 'word_problem' ? form.wordProblemType : undefined,
      };
    }

    if (!form.shortAnswer.trim()) {
      setValidationError('Correct answer is required.');
      return null;
    }

    return {
      ...baseQuestion,
      correct_answer: form.shortAnswer.trim(),
    };
  };

  const handleSave = async () => {
    const question = buildQuestion();
    if (!question) return;

    try {
      setSaving(true);
      setValidationError(null);
      await onSave(question);
      onClose();
    } catch (error: unknown) {
      setValidationError(
        error instanceof Error ? error.message : 'Failed to create practice question.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {validationError && <Alert severity="error">{validationError}</Alert>}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Question Type</InputLabel>
              <Select
                value={form.questionType}
                label="Question Type"
                onChange={(event: SelectChangeEvent) =>
                  updateField('questionType', event.target.value as QuestionType)
                }
              >
                <MenuItem value="multiple_choice">Multiple choice</MenuItem>
                <MenuItem value="true_false">True / false</MenuItem>
                <MenuItem value="short_answer">Short answer</MenuItem>
                <MenuItem value="numeric">Numeric</MenuItem>
                <MenuItem value="word_problem">Word problem</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={form.difficulty}
                label="Difficulty"
                onChange={(event: SelectChangeEvent) =>
                  updateField('difficulty', event.target.value as Difficulty)
                }
              >
                <MenuItem value="easy">Easy</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="hard">Hard</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Question Text
            </Typography>
            <RichTextEditor
              content={form.questionText}
              onChange={(value) => updateField('questionText', value)}
              placeholder="Write the question. Use the function button for LaTeX when needed."
              minHeight={160}
            />
          </Box>

          <TextField
            fullWidth
            label="Learning Objective"
            value={form.learningObjective}
            onChange={(event) => updateField('learningObjective', event.target.value)}
          />

          {form.questionType === 'multiple_choice' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="subtitle2">Answer Options</Typography>
              <RadioGroup
                value={String(form.correctOptionIndex)}
                onChange={(event) => updateField('correctOptionIndex', Number(event.target.value))}
              >
                {form.options.map((option, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'auto 1fr',
                      gap: 1,
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <FormControlLabel
                      value={String(index)}
                      control={<Radio />}
                      label={String.fromCharCode(65 + index)}
                      sx={{ mr: 0 }}
                    />
                    <TextField
                      fullWidth
                      label={`Option ${String.fromCharCode(65 + index)}`}
                      value={option}
                      onChange={(event) => updateOption(index, event.target.value)}
                    />
                  </Box>
                ))}
              </RadioGroup>
            </Box>
          )}

          {form.questionType === 'true_false' && (
            <FormControl>
              <Typography variant="subtitle2">Correct Answer</Typography>
              <RadioGroup
                row
                value={String(form.trueFalseAnswer)}
                onChange={(event) => updateField('trueFalseAnswer', event.target.value === 'true')}
              >
                <FormControlLabel value="true" control={<Radio />} label="True" />
                <FormControlLabel value="false" control={<Radio />} label="False" />
              </RadioGroup>
            </FormControl>
          )}

          {form.questionType === 'short_answer' && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.feedbackEnabled}
                    onChange={(event) => updateField('feedbackEnabled', event.target.checked)}
                  />
                }
                label="Draft AI feedback for teacher review"
              />
              {form.feedbackEnabled && (
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Feedback rubric"
                  helperText="Required concepts, accepted alternatives, and common misconceptions. Students receive feedback after you publish it."
                  value={form.feedbackRubric}
                  onChange={(event) => updateField('feedbackRubric', event.target.value)}
                  inputProps={{ maxLength: 4000 }}
                />
              )}
            </Box>
          )}
          {form.questionType === 'short_answer' && (
            <TextField
              fullWidth
              label={answerLabel}
              value={form.shortAnswer}
              onChange={(event) => updateField('shortAnswer', event.target.value)}
            />
          )}

          {(form.questionType === 'numeric' || form.questionType === 'word_problem') && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                label={answerLabel}
                type="number"
                value={form.numericAnswer}
                onChange={(event) => updateField('numericAnswer', event.target.value)}
              />
              <TextField
                fullWidth
                label="Tolerance"
                type="number"
                value={form.tolerance}
                onChange={(event) => updateField('tolerance', event.target.value)}
              />
              <TextField
                fullWidth
                label="Unit"
                value={form.unit}
                onChange={(event) => updateField('unit', event.target.value)}
              />
            </Box>
          )}

          {form.questionType === 'word_problem' && (
            <Box
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2 }}
            >
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Word Problem Context"
                value={form.wordProblemContext}
                onChange={(event) => updateField('wordProblemContext', event.target.value)}
              />
              <FormControl fullWidth>
                <InputLabel>Problem Type</InputLabel>
                <Select
                  value={form.wordProblemType}
                  label="Problem Type"
                  onChange={(event: SelectChangeEvent) =>
                    updateField('wordProblemType', event.target.value as WordProblemType)
                  }
                >
                  <MenuItem value="single_step">Single step</MenuItem>
                  <MenuItem value="multi_step">Multi step</MenuItem>
                  <MenuItem value="real_world">Real world</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          <Box
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' }, gap: 2 }}
          >
            <TextField
              fullWidth
              label="Points"
              type="number"
              value={form.points}
              onChange={(event) => updateField('points', event.target.value)}
            />
            <TextField
              fullWidth
              label="Hints"
              placeholder="One hint per line"
              value={form.hints}
              onChange={(event) => updateField('hints', event.target.value)}
            />
          </Box>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Explanation"
            value={form.explanation}
            onChange={(event) => updateField('explanation', event.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} /> : <Add />}
        >
          {saving ? 'Adding...' : 'Add Question'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
